import { useRef } from 'react';
import { Pill } from '../../components/ui/Pill';
import { useKeepFocusWhenGone } from '../../components/ui/useKeepFocusWhenGone';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { packTitle } from './animationScope';

interface Props {
  /** The pack being edited; absent in All packs scope. */
  pack: PoolOfferConfig | undefined;
  busy: boolean;
  onUseGlobal: () => void;
}

/** Whose opening the editor holds, and whether the pack already has one of its own. */
export function ScopeBanner({ pack, busy, onUseGlobal }: Props) {
  const custom = Boolean(pack?.animation);
  const sectionRef = useRef<HTMLElement>(null);
  // "Use global animation" goes away once it worked
  useKeepFocusWhenGone(custom, sectionRef);

  if (!pack) {
    return (
      <p className="mb-4 text-[12px] text-[var(--co-muted)]">
        Every pack without a custom opening plays this animation.
      </p>
    );
  }

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-label="Opening scope"
      className="mb-4 rounded-md border border-[var(--co-border)] p-3 outline-none"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[13px] font-semibold text-[var(--co-text)]">
          {packTitle(pack)}
        </span>
        <Pill tone={custom ? 'brand' : 'neutral'}>
          {custom ? 'Custom opening' : 'Uses global animation'}
        </Pill>
      </div>
      <p className="mt-1 text-[12px] text-[var(--co-muted)]">
        {custom
          ? 'This pack plays its own opening in every template.'
          : 'Starts from the global animation. Save to give this pack its own opening.'}
      </p>
      {custom && (
        <button
          type="button"
          disabled={busy}
          onClick={onUseGlobal}
          className="mt-2 rounded border border-[var(--co-border)] px-2 py-1 text-[12px] text-[var(--co-text)] hover:bg-[var(--co-bg)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Use global animation
        </button>
      )}
    </section>
  );
}
